import React, { FC, ReactNode, useEffect, useState } from 'react';
import {
  AddCircleOutlined,
  HelpOutlined,
  RemoveCircleOutlined,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Collapse,
  ConfigProvider,
  Empty,
  Flex,
  Result,
  theme,
  Typography,
} from '@duxton-web/ui';
import { Divider, Tooltip } from 'antd';

import { ReorderComponent } from '../../../common/ReorderComponent';
import Service, { ConstraintType, RolloutTarget } from '../../../lib/App';
import { EmptyConstraint } from '../../../lib/PlannerDTOs';
import { useAppNavigate } from '@/utils/navigation';

import './RolloutEditorV2.css';

// RouteComponentProps/withRouter removed in v6

import { VALID_RANDOMISATION_FACETS } from '../../../common/RandomisationFacets';
import { FacetsSelect } from '../../../common/selects/FacetsSelect';
import DefaultTarget from './DefaultTarget';
import ExperimentBlock from './ExperimentBlock/ExperimentBlock';
import SearchRolloutBy, {
  MatchInfo,
  SEARCH_BY,
  SearchRolloutByOnChangeEvent,
} from './SearchRolloutBy';
import { AccessControlWrapper } from '@/common/components';
import facetMap, {
  ConstraintOperator,
} from '@/common/ConstraintsEditor/Constraint/facetMap';
import { getDomainFamily } from '@/common/domains';
import { isMultiEnvEnabled } from '@/common/environment';
import EnvironmentToggle from '@/components/environment-toggle';
import { VariableState, VariableType } from '@/constants';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { useAppContext } from '@/contexts/app-context';
import { useRolloutContext } from '@/contexts/RolloutContext';
import { useVariable } from '@/contexts/VariableContext';
import Target from '@/features/rollout/rollout-target';
import { RolloutTargetsHeader } from '@/features/rollout/rollout-targets/header';
import { FacetType } from '@/pages/Search/Variables/VariablesSearch';
import { filterRolloutTargetMatchingFacet } from '@/pages/Search/Variables/VariablesSearchRollout';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';

export interface RolloutEditorProps {
  disabled?: boolean;
  isDiff?: boolean;
  oldDefaultTarget?: RolloutTarget;
  sectionHeader?: string;
  className?: string;
  isReordered?: (rolloutTarget: RolloutTarget) => boolean;
  // rollout and onChange now come from context
}

let timer = null;

type OwnProps = RolloutEditorProps;

export function customCollapseIcon({ isActive }): ReactNode {
  if (isActive) return <RemoveCircleOutlined />;

  return <AddCircleOutlined />;
}

const RolloutEditorV2: FC<OwnProps> = ({
  disabled = false,
  isDiff = false,
  oldDefaultTarget = null,
  className = '',
  isReordered = () => false,
  // v6: do not rely on router props here
}) => {
  // Get variable and rollout from context
  const variable = useVariable();
  const { rollout, handleRolloutChange: onChange } = useRolloutContext();
  const { navigateTo } = useAppNavigate();
  const { environment } = useAppContext();

  const isDomainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
  const disabledFacets = Service.cache!.getAllFacets()
    .filter((f) => !VALID_RANDOMISATION_FACETS.includes(f.name))
    .map((f) => f.name);
  const [activePanels, setActivePanels] = useState<string[]>(['-1']);
  const [expandRolloutTargets, setExpandRolloutTargets] =
    useState<boolean>(false);
  const [enableReorder, setEnableReorder] = useState<boolean>(false);
  const [targetIndexToHighlight, setTargetIndexToHighlight] =
    useState<number>(-2);
  const [searchByConfig, setSearchByConfig] =
    useState<SearchRolloutByOnChangeEvent>({
      searchBy: SEARCH_BY.CONSTRAINTS,
      value: '',
      constraints: [
        {
          target: '',
          op: '=',
          type: ConstraintType.Standard,
        },
      ],
    });
  const [matchInfo, setMatchInfo] = useState<MatchInfo>({
    matchRolloutTargetsCount: 0,
    totalRolloutTargets: 0,
    matchDefaultCount: 0,
  });
  const [missingMandatoryFacetsWarning, setMissingMandatoryFacetsWarning] =
    useState<{
      show: boolean;
      targetsCount: number;
      missingFacets: string[];
    }>({
      show: false,
      targetsCount: 0,
      missingFacets: [],
    });

  // Convert facet keys to readable names
  function getFacetDisplayName(facetKey: string): string {
    const allFacets = Service.cache!.getAllFacets();
    const facet = allFacets.find((f) => f.name === facetKey);
    return facet?.title || facetKey;
  }

  // Get the first available operator for a facet
  function getFirstOperatorForFacet(facet: string): string {
    if (facetMap?.[facet]?.op && facetMap[facet].op.length > 0) {
      return facetMap[facet].op[0].value || facetMap[facet].op[0].label;
    }

    // Default fallback to 'in' for most facets, or '=' if not available
    return (
      ConstraintOperator.IN?.value || ConstraintOperator.EQUALS?.value || '='
    );
  }

  // Create mandatory constraints based on variable mandatory facets
  function createMandatoryConstraints() {
    const mandatoryFacets = variable?.metadata?.mandatoryFacets;
    if (!mandatoryFacets || !mandatoryFacets.length) {
      return [];
    }

    return mandatoryFacets.map((facet) => {
      const constraint = EmptyConstraint();
      constraint.target = facet;
      constraint.op = getFirstOperatorForFacet(facet);
      constraint.value = '';
      constraint.type = ConstraintType.Standard;
      return constraint;
    });
  }

  // Check if existing rollout targets are missing mandatory facets
  function checkMissingMandatoryFacets() {
    const mandatoryFacets = variable?.metadata?.mandatoryFacets;
    if (
      !mandatoryFacets ||
      !mandatoryFacets.length ||
      !rollout?.rollouts?.length
    ) {
      setMissingMandatoryFacetsWarning({
        show: false,
        targetsCount: 0,
        missingFacets: [],
      });
      return;
    }

    let targetsWithMissingFacets = 0;
    const allMissingFacets = new Set<string>();

    rollout.rollouts.forEach((target) => {
      const existingConstraintFacets =
        target.constraints?.map((c) => c.target) || [];
      const missingFacets = mandatoryFacets.filter(
        (facet) => !existingConstraintFacets.includes(facet),
      );

      if (missingFacets.length > 0) {
        targetsWithMissingFacets++;
        missingFacets.forEach((facet) => allMissingFacets.add(facet));
      }
    });

    setMissingMandatoryFacetsWarning({
      show: targetsWithMissingFacets > 0,
      targetsCount: targetsWithMissingFacets,
      missingFacets: Array.from(allMissingFacets).map((facet) =>
        getFacetDisplayName(facet),
      ),
    });
  }

  // Auto-add missing mandatory facets to all rollout targets
  function addMissingMandatoryFacetsToAllTargets() {
    const mandatoryFacets = variable?.metadata?.mandatoryFacets;
    if (
      !mandatoryFacets ||
      !mandatoryFacets.length ||
      !rollout?.rollouts?.length
    ) {
      return;
    }

    const updatedRollouts = rollout.rollouts.map((target) => {
      const existingConstraintFacets =
        target.constraints?.map((c) => c.target) || [];
      const missingFacets = mandatoryFacets.filter(
        (facet) => !existingConstraintFacets.includes(facet),
      );

      if (missingFacets.length > 0) {
        const newConstraints = missingFacets.map((facet) => {
          const constraint = EmptyConstraint();
          constraint.target = facet;
          constraint.op = getFirstOperatorForFacet(facet);
          constraint.value = '';
          constraint.type = ConstraintType.Standard;
          return constraint;
        });

        return {
          ...target,
          constraints: [...newConstraints, ...(target.constraints || [])],
        };
      }

      return target;
    });

    onChange({
      ...rollout,
      rollouts: updatedRollouts,
    });

    setMissingMandatoryFacetsWarning({
      show: false,
      targetsCount: 0,
      missingFacets: [],
    });
  }

  function resetTargetIndexToHighlight() {
    clearInterval(timer);
    timer = setTimeout(() => {
      // setTargetIndexToHighlight(-2);
      navigateTo(`/${getDomainFamily()}/variables/${variable.name}/rollout`, {
        replace: true,
      });
    }, 5000);
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('highlightTargetId')) {
      const rolloutTargetIndex = parseInt(
        urlParams.get('highlightTargetId'),
        10,
      );

      setTargetIndexToHighlight(rolloutTargetIndex - 1);
      resetTargetIndexToHighlight();
    } else if (urlParams.has('expand')) {
      const rolloutTargetIndex = parseInt(urlParams.get('expand'), 10);

      setActivePanels([(rolloutTargetIndex - 1).toString()]);

      const target = document.getElementById(
        `rollot-editor-target-${rolloutTargetIndex - 1}`,
      );

      if (target) target.focus();

      setTimeout(() => {
        navigateTo(`/${getDomainFamily()}/variables/${variable.name}/rollout`, {
          replace: true,
        });
      }, 5000);
    }

    if (urlParams.has('id')) {
      const rolloutID = urlParams.get('id');

      setSearchByConfig({
        searchBy: SEARCH_BY.ID,
        value: rolloutID,
      });

      setTimeout(() => {
        navigateTo(`/${getDomainFamily()}/variables/${variable.name}/rollout`, {
          replace: true,
        });
      }, 100);
    }

    return () => {
      clearInterval(timer);
    };
  }, [location]);

  useEffect(() => {
    const rolloutTargets = rollout?.rollouts || [];
    const defaultTarget = rollout?.default || null;

    const matchRolloutTargetsCount = rolloutTargets.filter(
      (rolloutTarget: RolloutTarget) => {
        return filterRolloutTargetBySearchByConfig(rolloutTarget);
      },
    ).length;

    let matchDefaultTargetCount = 0;

    if (defaultTarget && filterRolloutTargetBySearchByConfig(defaultTarget))
      matchDefaultTargetCount = 1;

    setMatchInfo({
      matchRolloutTargetsCount: matchRolloutTargetsCount,
      totalRolloutTargets: rolloutTargets.length,
      matchDefaultCount: matchDefaultTargetCount,
    });
  }, [searchByConfig, rollout?.rollouts, rollout?.default]);

  // Check for missing mandatory facets whenever rollout or variable changes
  useEffect(() => {
    checkMissingMandatoryFacets();
  }, [rollout, variable?.metadata?.mandatoryFacets]);

  function handleNewTarget(e: React.MouseEvent<HTMLElement, MouseEvent>): void {
    e.stopPropagation();
    e.preventDefault();

    // Pre-populate mandatory constraints based on variable domain
    const mandatoryConstraints = createMandatoryConstraints();

    let newTarget: RolloutTarget = {
      constraints: mandatoryConstraints,
      type: 'none',
      string: '',
      value: 0,
      script: '',
    };

    onChange({
      ...rollout,
      // rollout without a history of being approved do not have '.rollouts' key, hence use [] first
      rollouts: [...(rollout.rollouts || []), newTarget],
    });
  }

  function handleDeleteTarget(targetIndex: number): void {
    onChange({
      ...rollout,
      rollouts: (rollout.rollouts || []).filter(
        (_, index) => index !== targetIndex,
      ),
    });
  }

  function handleTargetChange(
    targetIndex: number,
    rolloutTarget: RolloutTarget,
  ): void {
    onChange({
      ...rollout,
      rollouts: Object.assign([], rollout?.rollouts || [], {
        [targetIndex]: {
          ...rolloutTarget,
          constraints: rolloutTarget?.constraints?.filter(
            (constraint) => constraint?.op != '/' || constraint?.value != '1',
          ),
        },
      }),
    });
  }

  function handleDefaultTargetChange(rolloutTarget: RolloutTarget): void {
    onChange({
      ...rollout,
      default: rolloutTarget,
    });
  }

  function updateActivePanels(fromIndex: number, toIndex: number) {
    const activePanelsClone = [].concat(activePanels);

    if (activePanelsClone.includes(String(fromIndex))) {
      const filteredActivePanels = activePanelsClone.filter(
        (activePanel) => activePanel !== String(fromIndex),
      );
      filteredActivePanels.push(String(toIndex));
      return filteredActivePanels;
    }

    return activePanelsClone;
  }

  function updateRolloutTargets(
    rollouts: Array<RolloutTarget>,
    fromIndex: number,
    toIndex: number,
  ) {
    const { rollouts: oldRollouts, ...restRollout } = rollout;

    rollouts[toIndex].reorder = true;

    onChange({ ...restRollout, rollouts });

    setActivePanels(updateActivePanels(fromIndex, toIndex));

    navigateTo(
      `/${getDomainFamily()}/variables/${
        variable.name
      }/rollout?highlightTargetId=${toIndex + 1}`,
      { replace: true },
    );
  }

  function handleAssignByChange(
    oldAssignBy: string,
    newAssignBy: string,
  ): void {
    // update all constraints
    (rollout?.rollouts || []).map((rolloutTarget) => {
      const cindex = rolloutTarget?.constraints?.findIndex((c) => {
        return c.op == '/' && c.target == oldAssignBy;
      });
      const val = cindex != -1 ? rolloutTarget.constraints[cindex].value : '1';
      if (val != '1') {
        Object.assign(rolloutTarget.constraints[cindex], {
          target: newAssignBy,
          op: '/',
          value: val,
        });
      }
    });
    onChange({
      ...rollout,
      rollouts: rollout.rollouts || [],
      assignBy: newAssignBy,
    });
  }

  function handlePanelToggle(key: string): void {
    const activePanelsClone = [].concat(activePanels);

    if (activePanelsClone.includes(key)) {
      const filteredActivePanels = activePanelsClone.filter(
        (activePanel) => activePanel !== key,
      );

      setActivePanels(filteredActivePanels);

      return;
    }

    setActivePanels([key, ...activePanelsClone]);
  }

  function toggleRolloutTargetsPanel(
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ): void {
    e.stopPropagation();

    const toExpandRolloutTargets = !expandRolloutTargets;

    setExpandRolloutTargets(toExpandRolloutTargets);

    if (!toExpandRolloutTargets) {
      setActivePanels([]);

      return;
    }

    const panelsToCollapse = ['-1'];

    if (rollout?.rollouts?.length)
      for (let i = 0; i < rollout?.rollouts.length; i += 1) {
        panelsToCollapse.push(String(i));
      }

    setActivePanels(panelsToCollapse);
  }

  function toggleEnableReorder(e: React.MouseEvent<HTMLElement, MouseEvent>) {
    e.stopPropagation();

    setEnableReorder(!enableReorder);
  }

  function filterRolloutTargetBySearchByConfig(
    rolloutTarget: RolloutTarget,
  ): boolean {
    const { searchBy, value: searchByValue, constraints } = searchByConfig;

    if (searchByValue?.length > 0) {
      switch (searchBy) {
        case SEARCH_BY.ID: {
          return rolloutTarget?.id === searchByValue;
        }
        case SEARCH_BY.VALUE: {
          const variableType = variable?.type;

          switch (variableType) {
            case VariableType.FLOAT64:
            case VariableType.INT64:
              return String(rolloutTarget?.value)?.includes(searchByValue);
            case VariableType.BOOLEAN:
              return String(rolloutTarget?.value) === searchByValue;
            default:
              return rolloutTarget?.string?.includes(searchByValue);
          }
        }
      }
    }

    if (searchBy === SEARCH_BY.CONSTRAINTS) {
      if (!constraints?.[0]?.target) return true;

      const { target, value } = constraints[0];
      const isMatchingTarget = filterRolloutTargetMatchingFacet(
        [rolloutTarget],
        [
          {
            constraint: {
              facet: target as FacetType,
              value,
            },
          },
        ],
        0,
        10,
      );

      return isMatchingTarget.size > 0;
    }

    return true;
  }

  function showSearchByContraints(
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) {
    e.stopPropagation();
    e.preventDefault();

    navigateTo(
      `/${getDomainFamily()}/variables/${
        variable.name
      }/rollout/search-by-constraints`,
    );
  }

  function renderDefaultRolloutTarget(): ReactNode {
    return (
      <AccessControlWrapper
        action={UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
        techFamilies={[variable.techFamily]}
        accessTargets={[buildAccessTargetFromVariable(variable)]}
        showTooltip={false}
      >
        {(hasAccess) => (
          <DefaultTarget
            rollout={rollout}
            oldDefaultTarget={oldDefaultTarget}
            targetIndexToHighlight={targetIndexToHighlight}
            activePanels={activePanels}
            handlePanelToggle={handlePanelToggle}
            disabled={disabled || !hasAccess}
            enableReorder={enableReorder}
            handleDefaultTargetChange={handleDefaultTargetChange}
            isDiff={isDiff}
          />
        )}
      </AccessControlWrapper>
    );
  }

  function renderDescriptionBySearchBy() {
    switch (searchByConfig.searchBy) {
      case SEARCH_BY.CONSTRAINTS:
        return `There are no rollouts found for the constraint searched`;
      case SEARCH_BY.VALUE:
        return `There are no rollouts found for the value searched${matchInfo.matchDefaultCount ? '. However, the value matches the Default Value below.' : ''}`;
      case SEARCH_BY.ID:
        return `There are no rollouts found for the rollout ID searched${matchInfo.matchDefaultCount ? '. However, the rollout ID matches the Default Value below.' : ''}`;
      default:
        return 'No data';
    }
  }

  function renderEmpty() {
    if (matchInfo.matchRolloutTargetsCount) return false;

    if (!rollout?.rollouts?.length) return false;

    return (
      <Result
        icon={Empty.PRESENTED_IMAGE_SIMPLE}
        subTitle={renderDescriptionBySearchBy()}
        extra={
          <Button
            size="small"
            type="primary"
            style={{
              width: 'fit-content',
            }}
            onClick={() => {
              setSearchByConfig({
                ...searchByConfig,
                value: '',
                constraints: [
                  {
                    target: '',
                    op: '=',
                    type: ConstraintType.Standard,
                  },
                ],
              });
            }}
          >
            Clear Search
          </Button>
        }
      />
    );
  }

  function renderCreateRolloutButton(): ReactNode {
    const managedByPlatform = variable?.metadata?.managedByPlatform;
    const isManagedByPlatform = Boolean(managedByPlatform);

    const renderButton = (hasAccess: boolean) => {
      const isDisabled =
        !hasAccess ||
        variable?.state === VariableState.ARCHIVED ||
        isManagedByPlatform;

      const button = (
        <Button
          size="small"
          onClick={handleNewTarget}
          type="secondary"
          style={{ width: 'fit-content' }}
          disabled={isDisabled}
        >
          {`Create Rollout in ${environment === SystemEnvironment.PRODUCTION ? 'Production' : 'Staging'}`}
        </Button>
      );

      if (isManagedByPlatform) {
        return (
          <Tooltip title={`Rollout is managed by ${managedByPlatform}`}>
            <span>{button}</span>
          </Tooltip>
        );
      }

      return button;
    };

    return (
      <AccessControlWrapper
        action={UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
        techFamilies={[variable.techFamily]}
        accessTargets={[buildAccessTargetFromVariable(variable)]}
        showTooltip={false}
      >
        {renderButton}
      </AccessControlWrapper>
    );
  }

  function renderRolloutTargets(
    rolloutTargets: ReadonlyArray<RolloutTarget>,
    changeOrder: (fromIndex: number, toIndex: number) => void,
  ): ReactNode {
    if (!rolloutTargets?.length)
      return (
        <Result
          icon={
            <img
              src={`${process.env.PUBLIC_URL}/images/flash.webp`}
              height={144}
            />
          }
          title={'No Rollouts Yet'}
          subTitle={
            <Typography.Text style={{ color: '#707070' }}>
              No rollouts have been created for this variable yet.
            </Typography.Text>
          }
          extra={renderCreateRolloutButton()}
        />
      );

    if (!matchInfo.matchRolloutTargetsCount) return false;

    const items = rolloutTargets.map((rolloutTarget, index) => {
      if (!filterRolloutTargetBySearchByConfig(rolloutTarget)) return false;

      return (
        <Target
          key={`rollout-${index}`}
          rolloutTarget={rolloutTarget}
          maxPosition={rollout?.rollouts?.length || 0}
          index={index}
          handlePanelToggle={handlePanelToggle}
          enableReorder={enableReorder}
          disabled={disabled}
          isReordered={isReordered}
          handleDeleteTarget={handleDeleteTarget}
          changeOrder={changeOrder}
          activePanels={activePanels}
          targetIndexToHighlight={targetIndexToHighlight}
          isDiff={isDiff}
          handleTargetChange={handleTargetChange}
          assignBy={rollout?.assignBy}
        />
      );
    });

    return (
      <div
        id="rollout-editor-rollout-targets"
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items}
      </div>
    );
  }

  function renderSearchRolloutBy(): ReactNode {
    if (!rollout?.rollouts?.length) return false;

    return (
      <SearchRolloutBy
        config={searchByConfig}
        onChange={(e) => {
          setActivePanels([]);
          setSearchByConfig(e);
        }}
        matchInfo={matchInfo}
      />
    );
  }

  return (
    <div id="rollout-editor" className={className}>
      {isDomainlessFeatureFlag && (
        <div className="rollout-editor__randmisation_unit">
          <h4>
            Assign population of rollout with randomisation facet&nbsp;
            <Tooltip
              placement="topRight"
              title="This is a new feature to allow users to select randomisation facet for rollouts. Users will have more control on how their rollouts will be randomised as we move away from domains. More detail about domainless can be found by clicking the icon."
            >
              <a
                href="https://wiki.grab.com/display/EP/Domainless"
                target="blank"
              >
                <HelpOutlined />
              </a>
            </Tooltip>
          </h4>
          <FacetsSelect
            allowClear={false}
            value={rollout?.assignBy}
            availableFacets={variable?.facets}
            disabledFacets={disabledFacets}
            possibleFacets={variable?.facets}
            shouldFilterFacetsOnlyWhenEventLogDataIsPresent={true}
            onChange={(value) =>
              handleAssignByChange(rollout?.assignBy || '', value)
            }
            placeholder="Please select facet"
          />
          <Divider orientation="left" dashed />
        </div>
      )}

      {/* Warning banner for missing mandatory facets */}
      {missingMandatoryFacetsWarning.show && !disabled && (
        <Alert
          message="Missing Mandatory Facets"
          description={
            <div>
              <p>
                This variable requires mandatory facets{' '}
                <strong>
                  {(() => {
                    const allMandatoryFacets = (
                      variable?.metadata?.mandatoryFacets || []
                    ).map((facet) => getFacetDisplayName(facet));

                    // List all mandatory facets: "A, B and C"
                    if (allMandatoryFacets.length === 1) {
                      return allMandatoryFacets[0];
                    } else if (allMandatoryFacets.length === 2) {
                      return allMandatoryFacets.join(' and ');
                    } else {
                      return (
                        allMandatoryFacets.slice(0, -1).join(', ') +
                        ' and ' +
                        allMandatoryFacets[allMandatoryFacets.length - 1]
                      );
                    }
                  })()}
                </strong>{' '}
                in all rollout targets.{' '}
                {missingMandatoryFacetsWarning.targetsCount} target
                {missingMandatoryFacetsWarning.targetsCount === 1
                  ? ''
                  : 's'}{' '}
                {missingMandatoryFacetsWarning.targetsCount === 1
                  ? 'is'
                  : 'are'}{' '}
                missing mandatory facets.
              </p>
              <Button
                type="primary"
                size="small"
                onClick={addMissingMandatoryFacetsToAllTargets}
                style={{ marginTop: 8 }}
              >
                Add Missing Facets to All Targets
              </Button>
            </div>
          }
          type="warning"
          showIcon
          closable
          onClose={() =>
            setMissingMandatoryFacetsWarning({
              show: false,
              targetsCount: 0,
              missingFacets: [],
            })
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Flex vertical gap={16}>
        {isMultiEnvEnabled() && (
          <Card
            styles={{
              body: {
                padding: 20,
              },
            }}
          >
            <EnvironmentToggle />
          </Card>
        )}
        <ExperimentBlock variable={variable} />
        <ReorderComponent
          // rollout without a history of being approved do not have '.rollouts' key, hence use [] first
          orderedItems={[...(rollout.rollouts || [])]}
          onOrderedItemsChange={updateRolloutTargets}
          renderOrderedItems={(rolloutTargets, changeOrder) => {
            return (
              <ConfigProvider
                theme={{
                  components: {
                    Collapse: {
                      headerPadding: 16,
                    },
                  },
                }}
              >
                <ConfigProvider
                  theme={{
                    components: {
                      Collapse: {
                        contentPadding: 0,
                      },
                    },
                  }}
                >
                  <Collapse
                    expandIconPosition="end"
                    defaultActiveKey={'1'}
                    style={{
                      backgroundColor: theme.getDesignToken().white,
                    }}
                  >
                    <Collapse.Panel
                      key="1"
                      header={
                        <RolloutTargetsHeader
                          disabled={disabled}
                          expandRolloutTargets={expandRolloutTargets}
                          matchInfo={matchInfo}
                          showSearchByContraints={showSearchByContraints}
                          toggleRolloutTargetsPanel={toggleRolloutTargetsPanel}
                          toggleEnableReorder={toggleEnableReorder}
                          handleNewTarget={handleNewTarget}
                          enableReorder={enableReorder}
                        />
                      }
                    >
                      <Flex vertical>
                        {renderSearchRolloutBy()}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            backgroundColor: theme.getDesignToken().white,
                            borderBottomRightRadius: 12,
                            borderBottomLeftRadius: 12,
                          }}
                        >
                          {renderRolloutTargets(rolloutTargets, changeOrder)}
                          {renderEmpty()}
                        </div>
                      </Flex>
                    </Collapse.Panel>
                  </Collapse>
                </ConfigProvider>
              </ConfigProvider>
            );
          }}
        />

        {renderDefaultRolloutTarget()}
      </Flex>
    </div>
  );
};

export default RolloutEditorV2;
